/**
 * Supabase Calendar Service
 *
 * Supabase-backed implementation of {@link ICalendarService} — the only
 * calendar data implementation, exposed via
 * {@link module:features/family-calendar/services/calendarService}.
 *
 * Mapping notes (app camelCase ⇄ DB snake_case):
 *  - Calendar.coverImage ⇄ calendars.cover_image, defaultView ⇄ default_view,
 *    weekStartsOn ⇄ week_starts_on, memberIds ⇄ member_ids
 *  - CalendarMember.memberId ⇄ calendar_members.profile_id,
 *    joinedAt ⇄ calendar_members.created_at
 *  - CalendarEvent.startDate ⇄ events.start_date (timestamptz), etc.
 *
 * Storage notes:
 *  - EventLabel.emoji ⇄ calendar_labels.emoji, EventLabel.sortOrder ⇄
 *    calendar_labels.sort_order. Both are real columns as of migration 028;
 *    before it, `sortOrder` was derived from creation order on read and
 *    `emoji` was echoed back out of the DTO and lost on the next server read.
 *  - CalendarEvent.attachments: persisted to / read from the `event_attachments`
 *    table; files live in the private `attachments` storage bucket and are
 *    served via short-lived signed URLs.
 *
 * ENUM COLUMNS REJECT, THEY DO NOT SUBSTITUTE
 *   `memos.color` is the six-value `memo_color` enum and
 *   `anniversaries.category` the seven-value `anniversary_category` enum.
 *   Both writers used to coerce an unrecognised value to a seed
 *   (`: 'yellow'` / `: 'other'`), which turned "this palette cannot be
 *   stored" into a silent repaint one round trip after a success toast — the
 *   user picked red, the toast said saved, the card came back yellow. The
 *   coercion is also what let the mismatch survive: nothing ever failed, so
 *   nothing was ever reported. {@link memoColorForColumn} and
 *   {@link anniversaryCategoryForColumn} now THROW instead, and the pickers
 *   upstream (CreateMemoModal, CreateAnniversaryModal) offer only the values
 *   these two functions accept — so the throw is a bug detector, not a path a
 *   user can walk into.
 *
 * SIGNING, AND WHY THE MAPPER TAKES A LOOKUP
 *   `attachments` is a private bucket, so `event_attachments.file_url` holds a
 *   RAW OBJECT KEY and every read path has to mint a signed URL before a
 *   component sees the row. `rowToAttachment` used to be `async` and to sign
 *   its own row, which made ONE unreadable object fatal to the whole calendar:
 *   the rejection propagated out of `hydrateAttachments` and out of
 *   `getAllEvents`, and the user saw no events at all. It also failed
 *   `createEvent` / `updateEvent` for rows that had already committed.
 *
 *   So the mapper is SYNCHRONOUS and total, and `hydrateAttachments` signs the
 *   whole page in ONE request via `createSignedUrls` — the shape
 *   `supabaseAlbumService` already proved on the `photos` bucket. A key that
 *   will not sign is simply absent from the lookup, the attachment comes back
 *   with an empty `url`, and everything else on the page still renders.
 *
 * ATTACHMENT LIFECYCLE — THE ROW GOES FIRST, THE OBJECT FOLLOWS
 *   Dropping an attachment used to delete only its `event_attachments` row, so
 *   the file stayed in the private `attachments` bucket with nothing pointing
 *   at it and nothing ever collecting it (`cleanup-orphan-storage` sweeps
 *   `photos` and deliberately nothing else). `persistAttachments` and
 *   `deleteEvent` now remove the objects the event stopped referencing, and
 *   both do it in the same order: read the keys from the rows, write the rows,
 *   then delete the objects — best effort, reported through
 *   `monitoringService` and never allowed to fail the mutation. An object that
 *   outlives its row is a leak; a row that outlives its object is a permanently
 *   broken attachment, so the leak is the direction to fail in.
 *
 * OFFLINE WRITES, AND WHY THE GATE HAS TWO SIDES
 *   `navigator.onLine === false` is the cheap check and runs first, before
 *   anything touches the wire — including the profile lookup, which is why
 *   `createEvent` resolves its author through
 *   {@link SupabaseCalendarService.resolveProfileId}. `getCurrentProfileId`
 *   used to be an `auth.getUser()` round trip that rejected above the gate and
 *   made the enqueue below it unreachable; it reads the persisted session now,
 *   so both branches are local.
 *
 *   The expensive case is a browser reporting `onLine === true` over a captive
 *   portal or a dead uplink. postgrest-js does not reject there; it answers
 *   with an error envelope. So each write also classifies its own failure with
 *   {@link isTransientWriteFailure} and enqueues on the transport class only —
 *   never on a refusal, which would poison the replay queue for every table.
 *
 * @module features/family-calendar/services/supabaseCalendarService
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import {
  getCurrentFamilyId,
  getCurrentProfileId,
} from '@/shared/lib/familyContext';
import {
  enqueueSupabaseMutation,
  isTransientWriteFailure,
} from '@/shared/lib/offlineMutationQueue';
import { isPersistedId } from '@/shared/lib/persistedId';
import {
  sanitizeFilename,
  sanitizeText,
  sanitizeUrl,
} from '@/shared/lib/sanitize';
import { monitoringService } from '@/shared/services/monitoringService';
import {
  createSignedUrls,
  removeFiles,
  storageKeyForRemoval,
  storageKeyFromValue,
} from '@/shared/services/storageService';
import type {
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from '@/shared/types/database';
import type {
  Anniversary,
  AnniversaryCategory,
  AnniversaryCategoryRef,
  CreateAnniversaryDTO,
  UpdateAnniversaryDTO,
} from '../types/anniversary.types';
import { isAnniversaryCategory } from '../types/anniversary.types';
import type {
  Calendar,
  CalendarMember,
  CalendarMemberRole,
  EventLabel,
} from '../types/calendar.types';
import type {
  CalendarEvent,
  EventAttachment,
  EventChangeRecord,
  EventChangedField,
  EventComment,
  RecurrenceRule,
  RecurringEventUpdateScope,
  Reminder,
  SeriesInstants,
  SeriesTarget,
  SeriesWriteResult,
  ShareLink,
  ShareLinkExpiration,
  ShareLinkPermission,
} from '../types/event.types';
import {
  getFileTypeFromExtension,
  TRACKED_EVENT_FIELDS,
} from '../types/event.types';
import type {
  CreateMemoDTO,
  Memo,
  MemoColor,
  MemoColorRef,
  UpdateMemoDTO,
} from '../types/memo.types';
import { isMemoColor } from '../types/memo.types';
import { toLocalDateKey } from '../utils/date.utils';
import type {
  AddMemberDTO,
  CreateCalendarDTO,
  CreateEventDTO,
  CreateLabelDTO,
  DateRange,
  ICalendarService,
  UpdateCalendarDTO,
  UpdateEventDTO,
  UpdateLabelDTO,
  UpdateMemberOverridesDTO,
} from './calendarService.types';

type CalendarRow = Tables<'calendars'>;
type EventRow = Tables<'events'>;
type CalendarMemberRow = Tables<'calendar_members'>;
type CalendarLabelRow = Tables<'calendar_labels'>;
type EventAttachmentRow = Tables<'event_attachments'>;
type EventCommentRow = Tables<'event_comments'>;
type MemoRow = Tables<'memos'>;
type AnniversaryRow = Tables<'anniversaries'>;
type ShareLinkRow = Tables<'calendar_share_links'>;
type EventChangeRow = Tables<'event_changes'>;

/**
 * How many `event_changes` rows one История read is allowed to fetch.
 *
 * DELIBERATELY UNDER `[api] max_rows`, which `supabase/config.toml` sets to
 * 1000. An unbounded read is TRIMMED AT THE SERVER CAP WITH NO ERROR, and
 * combined with an ascending order that returns the OLDEST 1000 records —
 * dropping exactly the recent ones the feed exists to show. The read below is
 * therefore DESCENDING and explicitly bounded; `masterEventPaging.ts` and
 * `notify-event-reminder` document the identical trap, and
 * `referencePaging.ts` adds that `config.toml`'s value is not proof of the LIVE
 * one, which is a second reason to bound client-side.
 *
 * Accepted limitation: this is a hard horizon with no "load more" affordance.
 * Keyset paging is the fix if it ever matters.
 */
const EVENT_CHANGE_PAGE_SIZE = 900;

/**
 * The fields the change log renders. Rows outside it are DROPPED on read, not
 * cast: `event_changes.field` is shape-checked (`<> ''`, `length <= 64`) and
 * deliberately not vocabulary-checked, so an unknown value is possible and
 * `FIELD_LABELS[unknown]` would render a bare „:" followed by a diff.
 */
const TRACKED_FIELD_SET: ReadonlySet<string> = new Set(TRACKED_EVENT_FIELDS);

/** What one confirming SELECT after a zero-row DELETE can conclude. */
type ZeroRowDeleteVerdict = 'already-gone' | 'refused' | 'transient';

/**
 * Tell a REFUSED delete from one that was already satisfied.
 *
 * A DELETE that returns no rows and no error is ambiguous on the wire: RLS
 * filtered the row out, or somebody else deleted it first. The two demand
 * opposite handling, so one confirming SELECT resolves it — a row still
 * readable means the delete was refused, and a row that is gone means the
 * user's intent is already satisfied and the call resolves quietly.
 *
 * A FLAT "zero rows = refused" IS ITS OWN BUG. It would make the store's
 * rollback re-insert a row for a delete that actually succeeded, and it
 * contradicts the queue's deliberate asymmetry in `useOfflineQueue.ts`, where a
 * zero-row replay DELETE is a SUCCESS while a zero-row replay UPDATE is a
 * permanent failure. Do not "fix" one of them to match the other.
 *
 * This is a PRIVATE RE-IMPLEMENTATION of the identical helper in
 * `supabaseAlbumService.ts`, which is module-private there. Importing across
 * two feature services to share nine lines is the wrong trade; the shape is
 * what matters and both copies carry it.
 */
function classifyZeroRowDelete(
  stillReadable: boolean,
  error: { readonly message?: string } | null,
  status: number | undefined
): ZeroRowDeleteVerdict {
  if (error) {
    return isTransientWriteFailure(status, error) ? 'transient' : 'refused';
  }
  return stillReadable ? 'refused' : 'already-gone';
}

/**
 * Record that one edit's history batch was dropped rather than persisted.
 *
 * A breadcrumb, at `warning`, because the alternative shapes are both worse: a
 * throw fails an edit that IS going to land (see
 * {@link SupabaseCalendarService.createEventChanges}), and silence makes „the
 * feed is missing yesterday's rename" unexplainable months later. The next
 * `load()` replaces `eventChangeLog` wholesale, so the user-visible residue is
 * one missing row in История and nothing else.
 */
/**
 * What a REFUSED history row says out loud.
 *
 * It describes the failure accurately and stops there: the edit itself is
 * unaffected (the store no longer rolls it back — see `persistChangeRecordsSoft`
 * — because the PATCH or the RPC has already committed by the time this can
 * throw), so the sentence must not read as „your change was lost".
 */
const EVENT_CHANGE_WRITE_FAILED_MESSAGE =
  'Промяната беше запазена, но не можа да бъде записана в История.';

function reportChangeLogSkipped(
  reason: 'offline' | 'transport',
  detail: string
): void {
  monitoringService.captureError(
    new Error(`event_changes skipped (${reason}): ${detail}`),
    {
      tags: { feature: 'family-calendar', action: 'createEventChanges' },
      level: 'warning',
    }
  );
}

/**
 * The message both series RPC wrappers throw when the device is offline.
 *
 * REFUSAL, NOT QUEUEING, and the reason is structural rather than cautious. A
 * queue entry is `{table, op, payload?, matchId?}` replayed as
 * `.eq('id', matchId)`; an RPC is none of those things and `checkReplayable`
 * dead-letters it (destructive toast + Sentry). Worse, a queued exDates payload
 * would be a whole-jsonb read-modify-write with the READ taken at enqueue time,
 * so two devices cancelling two different occurrences replay two full-array
 * writes, the second silently clobbers the first, and the expander regenerates
 * an occurrence the family had confirmed cancelled — days later, with no trace.
 * Appending inside the locked UPDATE is the only correct form of that write and
 * no queued payload can express it.
 */
const SERIES_OFFLINE_MESSAGE =
  'Промяна на повтарящо се събитие изисква връзка с интернет.';

/**
 * Bulgarian copy for every REFUSAL code the two series RPCs raise, KEYED BY
 * CODE.
 *
 * ***  THE ONLY THING BETWEEN A PLPGSQL `raise` AND A BULGARIAN-ONLY FAMILY. ***
 * `error.message` from PostgREST is the raise's message text verbatim, and
 * `persistCalendarMutation` puts whatever this file throws straight into the
 * destructive toast's description. Unmapped, a refusal reaches a Bulgarian-only
 * app as raw English SQLSTATE P0001 prose about `events_parent_occurrence_uq`.
 *
 * KEYED BY CODE, NEVER BY MATCHING MESSAGE TEXT. 029 raises
 * `SERIES_INSTANT_OCCUPIED` from more than one guard, with a different sentence
 * each time and more of them being added; matching prose would map one raise
 * site and silently leak the next one. The code is the stable half of the
 * contract — 029's closing notice states it as one.
 *
 * WHAT EACH LINE HAS TO DO. These are REFUSALS, not failures: the transaction
 * rolled back, nothing was changed, and the family CAN complete the operation
 * after one specific step. So every line says what happened AND what to do
 * instead. Risk 15's lesson is the constraint — a refusal must not be reported
 * as something it is not, and „нещо се обърка" would report all three as a
 * crash the user can only retry.
 */
const SERIES_REFUSAL_MESSAGES: Readonly<Record<string, string>> = {
  // Unreachable from today's UI — nothing can author `interval <> 1` — and
  // mapped anyway, because the day a repeat-every-N picker lands this is the
  // first thing the user sees.
  //
  // ***  VERB-NEUTRAL, BECAUSE THE CODE IS. 029 raises both ANCHOR_ROLL_*  ***
  // ***  codes from the anchor-roll leg of `delete_event_series` AND from  ***
  // ***  the one in `update_event_series`.                                 ***
  // A sentence naming one verb therefore tells half the users about an
  // operation they did not attempt: „първото не може да бъде премахнато" told
  // somebody who pressed „Запази промените" that their DELETION was refused.
  // Neither code can tell the two apart — that is what makes them one code —
  // so the copy must be true for both. Only `SERIES_INSTANT_OCCUPIED` may name
  // a verb, and it names the right one: nothing but an EDIT can move an
  // occurrence onto an occupied instant, and a DELETE never raises it.
  //
  // ***  A DELIBERATE, RECORDED DEVIATION FROM CONTRACT DECISION 15.  ***
  // Decision 15 fixes the copy verbatim as „Тази поредица не може да бъде
  // променена от първото си повторение." — one sentence, and it names the EDIT
  // verb („променена"). That string was written when the refusal existed only
  // in `delete_event_series`; 029 ships it from both RPCs, so shipping it
  // verbatim would reintroduce, in the copy, the exact mis-report the block
  // above exists to prevent — a family member who pressed „Изтрий" being told
  // their CHANGE was refused. The shipped wording keeps decision 15's meaning
  // and adds the two things risk 15 requires of a refusal: it is true for both
  // verbs, and it says what to do instead. It is not drift; if decision 15 is
  // ever re-read as binding on the letter, this comment is the answer.
  ANCHOR_ROLL_INTERVAL:
    'Тази поредица не може да бъде променяна или изтривана от първото си ' +
    'повторение. Изберете друго повторение или „Всички повторения".',
  ANCHOR_ROLL_OCCUPIED:
    'Следващото повторение вече е променяно отделно, затова първото не може ' +
    'да отпадне — двете биха се озовали в един и същ момент. Изтрийте или ' +
    'преместете следващото повторение и опитайте отново.',
  SERIES_INSTANT_OCCUPIED:
    'В избрания момент вече стои друго повторение от същата поредица. Две ' +
    'повторения не могат да съвпадат — изберете друг час или дата.',
};

/**
 * SQLSTATE `23505` reached through either series RPC — the ONE refusal 029
 * does not raise itself.
 *
 * ***  IT IS REACHABLE, AND 029 SAYS SO IN WORDS.  ***
 * `update_event_series` E-a probes for a row at the target instant and INSERTS
 * when it finds none. The insert lands on `events_parent_occurrence_uq`
 * (`parent_event_id, start_date`) — so moving one occurrence onto an instant
 * where an AUTHORED exception already stands aborts the transaction with a
 * unique violation instead of a `raise`. Nothing is left half-written; the user
 * just gets told, and unmapped they were told in English, by Postgres,
 * complaining about an index name.
 *
 * MAPPED BY SQLSTATE, NOT BY MATCHING THE CONSTRAINT NAME IN THE PROSE. The
 * message text of a built-in error is a Postgres locale string, not a contract;
 * `error.code` is. And the mapping is safe to make on SQLSTATE alone BECAUSE IT
 * IS SCOPED TO THESE TWO FUNCTIONS: the only unique indexes their statements can
 * violate are `events_pkey` — which the tail insert defuses with
 * `on conflict (id) do nothing` — and `events_parent_occurrence_uq`. It is not
 * a general „23505 means this" rule and must not be reused as one.
 */
const SERIES_DUPLICATE_SQLSTATE = '23505';
const SERIES_DUPLICATE_MESSAGE =
  'В избрания момент вече стои повторение, което е било променяно отделно. ' +
  'Отворете него и го преместете или изтрийте, преди да преместите това.';

/**
 * What the user is told when a series write fails for anything NOT in
 * {@link SERIES_REFUSAL_MESSAGES}.
 *
 * DELIBERATELY VAGUE ABOUT WHAT SURVIVED. The unmapped set is everything from a
 * `row_count = 0` raise (transaction rolled back, nothing changed) to a dropped
 * connection on a call that may well have committed. Claiming „нищо не беше
 * променено" would be risk 15's mistake wearing Bulgarian: a message that
 * reports the failure as something it is not. So it says what is true in every
 * one of those cases — the operation did not complete as asked, and the current
 * state is on the server.
 */
const SERIES_WRITE_FAILED_MESSAGE =
  'Промяната на поредицата не можа да бъде извършена. Презаредете календара, ' +
  'за да видите текущото състояние, и опитайте отново.';

/**
 * The refusal code a raise's message text begins with, or `null`.
 *
 * ANCHORED AT THE START, and screaming-snake only. `raise exception 'CODE: …'`
 * is the shape 029 uses for all three; every other raise in those two functions
 * opens with the function name in lower case (`delete_event_series: …`) and is
 * correctly NOT a code. Searching the whole string instead would let a future
 * message that merely QUOTES a code — „…, which would raise
 * SERIES_INSTANT_OCCUPIED" — be mapped to copy about a refusal that did not
 * happen.
 */
function seriesRefusalCode(rawMessage: string): string | null {
  return /^([A-Z][A-Z0-9_]{2,63})(?::|$)/.exec(rawMessage)?.[1] ?? null;
}

/**
 * Bulgarian copy for a failed series RPC. NEVER the raw server text.
 *
 * An unrecognised code degrades to {@link SERIES_WRITE_FAILED_MESSAGE} rather
 * than leaking English — including a code a LATER migration adds, which is the
 * realistic case: 029 is still gaining raise sites. The raw text is not thrown
 * away with it; it goes to Sentry, so the untranslated refusal is a reported
 * gap rather than an invisible one.
 */
function seriesRefusalMessage(error: {
  readonly message: string;
  readonly code?: string | null;
}): string {
  const rawMessage = error.message;
  const code = seriesRefusalCode(rawMessage);
  const mapped = code === null ? undefined : SERIES_REFUSAL_MESSAGES[code];
  if (mapped !== undefined) return mapped;

  // The SQLSTATE arm is checked SECOND, so an explicit `raise` always wins: a
  // future 029 guard that catches the collision itself and raises a named code
  // would otherwise be shadowed by the generic duplicate copy.
  if (error.code === SERIES_DUPLICATE_SQLSTATE) {
    return SERIES_DUPLICATE_MESSAGE;
  }

  monitoringService.captureError(new Error(rawMessage), {
    tags: { feature: 'family-calendar', action: 'seriesWriteFailed' },
    extra: { code: code ?? error.code ?? 'none' },
    level: 'warning',
  });
  return SERIES_WRITE_FAILED_MESSAGE;
}

/** The private bucket every event attachment lives in. */
const ATTACHMENT_BUCKET = 'attachments';

/** Empty lookup, so an unsigned mapper call needs no null checks downstream. */
const NO_SIGNATURES: ReadonlyMap<string, string> = new Map();

// ============================================
// Mappers
// ============================================

/**
 * Object key for an attachment row — THE READ HELPER, and read only.
 *
 * `file_url` holds a raw key — FileUploadArea stores `uploadFile`'s `path` —
 * but it is coerced through `storageKeyFromValue` anyway, mirroring
 * `rowToPhoto`, so a row that somehow holds a full URL still resolves to
 * something signable. An unrecognisable value falls back to the stored string
 * verbatim rather than to `''`: `persistAttachments` writes `storagePath` back
 * into the `not null` `file_url` column, and blanking it would trade a bad
 * value for an invalid row.
 *
 * That lenient fallback is exactly what a DELETE must not have, so nothing on
 * a removal path may call this. `attachmentKeysFor` uses
 * `storageKeyForRemoval` instead — see the comment there, and the long one
 * above `storageKeyForRemoval` itself.
 */
function attachmentKey(row: EventAttachmentRow): string {
  return storageKeyFromValue(ATTACHMENT_BUCKET, row.file_url) || row.file_url;
}

/**
 * Map an `event_attachments` row to an {@link EventAttachment}, resolving its
 * signed URL from `signed`.
 *
 * SYNCHRONOUS AND TOTAL, which is the whole fix — see the module banner. A key
 * that is missing from the lookup (signing failed, the object is gone, the
 * policy refused, the row is an orphan left behind by `persistAttachments`'
 * delete-then-reinsert) yields `url: ''` and `isUnavailable: true`, and
 * everything else about the attachment — filename, size, type, who uploaded it
 * — still renders. `isUnavailable` is what the UI keys off: the empty string
 * alone was too easy to hand straight to `window.open`, which opens a blank
 * tab and explains nothing.
 *
 * `thumbnailUrl` is withheld along with `url`, so an image whose object cannot
 * be signed renders as a missing attachment rather than as a broken `<img>`.
 */
function rowToAttachment(
  row: EventAttachmentRow,
  signed: ReadonlyMap<string, string> = NO_SIGNATURES
): EventAttachment {
  const storagePath = attachmentKey(row);
  const url = signed.get(storagePath) ?? '';
  const mimeType = row.mime_type ?? 'application/octet-stream';
  const base: EventAttachment = {
    id: row.id,
    filename: row.file_name,
    url,
    storagePath,
    size: row.file_size ?? 0,
    mimeType,
    fileType: getFileTypeFromExtension(row.file_name),
    uploadedBy: row.uploaded_by,
    uploadedAt: row.created_at,
    ...(url === '' && { isUnavailable: true }),
  };
  return mimeType.startsWith('image/') && url !== ''
    ? { ...base, thumbnailUrl: url }
    : base;
}

/**
 * Sign every object referenced by a page of attachment rows, in ONE request.
 *
 * `createSignedUrls` dedupes and never throws, so a total failure comes back as
 * an empty lookup and the calendar still renders. N attachments cost one round
 * trip instead of N sequential ones.
 */
async function signAttachmentRows(
  rows: readonly EventAttachmentRow[]
): Promise<ReadonlyMap<string, string>> {
  return createSignedUrls(ATTACHMENT_BUCKET, rows.map(attachmentKey));
}

/**
 * Report the attachments a page could not sign — once per page, not once per
 * row.
 *
 * `createSignedUrls` swallows its own failures by design (a partial failure
 * must not cost the caller the other attachments), which would otherwise make
 * an unreadable object invisible to operations as well as survivable for the
 * user. One `captureError` carrying the count and the offending keys beats N
 * events for one dead bucket.
 */
function reportUnsignedAttachments(
  rows: readonly EventAttachmentRow[],
  signed: ReadonlyMap<string, string>
): void {
  const unsigned = rows.filter((row) => !signed.has(attachmentKey(row)));
  if (unsigned.length === 0) {
    return;
  }
  monitoringService.captureError(
    new Error(
      `Неуспешно подписване на ${unsigned.length} прикачен(и) файл(а) към събитие.`
    ),
    {
      tags: { feature: 'family-calendar', action: 'hydrateAttachments' },
      extra: {
        attachmentIds: unsigned.map((row) => row.id),
        storagePaths: unsigned.map(attachmentKey),
      },
      level: 'warning',
    }
  );
}

/**
 * `calendar_labels` row → {@link EventLabel}.
 *
 * TAKES NO `sortOrder` ARGUMENT ANY MORE, AND THAT IS THE POINT. It used to,
 * because the column did not exist: `loadLabels` passed the row's index in a
 * `created_at`-ordered page, and `updateLabel` — which reads one row and has
 * no page to index into — passed a literal `0`, so editing a label's name
 * quietly moved it to the front of the list. Migration 028 added
 * `sort_order integer not null default 0`, so the order now comes from the
 * row and survives both paths.
 *
 * `emoji` is nullable and the domain type spells "no emoji" as an ABSENT
 * property (`exactOptionalPropertyTypes` is on), hence the conditional
 * spread. An empty string is treated as absent too: `updateLabel` writes NULL
 * when the user clears the picker, but a row written before that normalisation
 * existed could still hold `''`, and an empty-string emoji would render as a
 * zero-width gap in the chip rather than as "no emoji".
 *
 * Exported for the same reason `rowToEvent` is: the mapping is pinned by its
 * own suite, which must be able to feed it a row without a Supabase client.
 */
export function rowToLabel(row: CalendarLabelRow): EventLabel {
  return {
    id: row.id,
    name: row.name,
    color: row.color,
    calendarId: row.calendar_id,
    sortOrder: row.sort_order,
    ...(row.emoji != null && row.emoji !== '' && { emoji: row.emoji }),
  };
}

/**
 * A label emoji as the `calendar_labels.emoji` column wants it.
 *
 * The column is nullable and NULL is the only spelling of "this label has no
 * emoji". The form hands over `''` when the user clears the picker — see
 * `LabelManager.handleSubmit`, which deliberately sends an empty string rather
 * than omitting the key, because an omitted key is indistinguishable from "not
 * edited" and made an emoji impossible to remove.
 */
function labelEmojiForColumn(value: string | null | undefined): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed === '' ? null : trimmed;
}

/**
 * A memo colour as the `memo_color` enum wants it, or a thrown error.
 *
 * NOT a coercion. `isMemoColor(x) ? x : 'yellow'` used to sit at both memo
 * write sites, and it is the reason the 13-swatch picker survived against a
 * six-value enum for as long as it did: every unstorable choice was accepted,
 * reported as saved, and repainted yellow on the next read. An unknown value
 * reaching here now means the picker and the enum have drifted apart, which is
 * a defect in this repo rather than something the user did — so it is raised
 * loudly (and rolled back by `persistCalendarMutation` with a destructive
 * toast) instead of being written as something else.
 *
 * `undefined` is not drift: the DTO's `color` is optional, "did not choose"
 * is a real answer, and the column's own default is `yellow`.
 */
function memoColorForColumn(value: MemoColorRef | undefined): MemoColor {
  if (value === undefined) {
    return 'yellow';
  }
  if (!isMemoColor(value)) {
    throw new Error(
      `Цветът „${String(value)}“ не се поддържа за бележки. Изберете цвят от палитрата.`
    );
  }
  return value;
}

/**
 * An anniversary category as the `anniversary_category` enum wants it, or a
 * thrown error. Same contract as {@link memoColorForColumn}, same reason: the
 * old `: 'other'` fallback repainted a tagged anniversary as „Друго“ one read
 * after the success toast.
 */
function anniversaryCategoryForColumn(
  value: AnniversaryCategoryRef
): AnniversaryCategory {
  if (!isAnniversaryCategory(value)) {
    throw new Error(
      `Категорията „${String(value)}“ не се поддържа за годишнини. Изберете категория от списъка.`
    );
  }
  return value;
}

function rowToCalendar(
  row: CalendarRow,
  labels: readonly EventLabel[]
): Calendar {
  return {
    id: row.id,
    name: row.name,
    memberIds: [...row.member_ids],
    labels: [...labels],
    isPublic: row.is_public,
    defaultView: row.default_view,
    weekStartsOn: row.week_starts_on === 0 ? 0 : 1,
    shareLinks: [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description != null && { description: row.description }),
    ...(row.cover_image != null && { coverImage: row.cover_image }),
  };
}

function rowToMember(row: CalendarMemberRow): CalendarMember {
  return {
    memberId: row.profile_id,
    calendarId: row.calendar_id,
    role: row.role,
    joinedAt: row.created_at,
    ...(row.display_name_override != null && {
      displayNameOverride: row.display_name_override,
    }),
    ...(row.avatar_override != null && {
      avatarOverride: row.avatar_override,
    }),
  };
}

function rowToComment(row: EventCommentRow): EventComment {
  return {
    id: row.id,
    eventId: row.event_id,
    text: row.text,
    authorId: row.author_id,
    createdAt: row.created_at,
    ...(row.updated_at !== row.created_at && { updatedAt: row.updated_at }),
  };
}

function rowToMemo(row: MemoRow): Memo {
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    calendarId: row.calendar_id,
    authorId: row.author_id,
    color: row.color,
    isPinned: row.is_pinned,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToAnniversary(row: AnniversaryRow): Anniversary {
  return {
    id: row.id,
    name: row.name,
    date: row.date,
    type: row.type,
    category: row.category,
    emoji: row.emoji,
    color: row.color,
    isRecurring: row.is_recurring,
    notifyOnDay: row.notify_on_day,
    calendarId: row.calendar_id,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description != null && { description: row.description }),
  };
}

function rowToShareLink(row: ShareLinkRow): ShareLink {
  const isExpired =
    row.expires_at !== null && new Date(row.expires_at).getTime() < Date.now();
  return {
    id: row.id,
    calendarId: row.calendar_id,
    url: `${window.location.origin}/shared/${row.token}`,
    code: row.token,
    permission: 'view',
    expiresAt: row.expires_at,
    createdAt: row.created_at,
    createdById: row.created_by,
    isActive: row.revoked_at === null && !isExpired,
    usageCount: 0,
  };
}

/** Map a UI expiration preset to an ISO timestamp (or null = never). */
function expirationToTimestamp(expiration: ShareLinkExpiration): string | null {
  if (expiration === 'never') {
    return null;
  }
  const expirationMs: Record<Exclude<ShareLinkExpiration, 'never'>, number> = {
    '1_hour': 60 * 60 * 1000,
    '24_hours': 24 * 60 * 60 * 1000,
    '7_days': 7 * 24 * 60 * 60 * 1000,
    '30_days': 30 * 24 * 60 * 60 * 1000,
  };
  return new Date(Date.now() + expirationMs[expiration]).toISOString();
}

/** Generate an unguessable share token (24 hex chars). */
function generateShareToken(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** True when the browser reports no network connectivity. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Map an `events` row to the domain {@link CalendarEvent}.
 *
 * OCCURRENCE PROVENANCE, AND WHY IT IS NOT OPTIONAL HERE
 *   Migration 006 added `parent_event_id` / `is_occurrence` and
 *   `expand-recurring-events` writes both on every row it materializes, but
 *   this mapper dropped them — so the client could not tell a generated
 *   occurrence from an event a family member typed in. It cannot fall back on
 *   `recurrence` either: the expander deliberately writes `recurrence: null`
 *   onto occurrences, because only the master expands. The result was an
 *   occurrence that rendered as an ordinary one-off event and that came BACK
 *   after being deleted, with nothing in the UI able to explain why.
 */
export function rowToEvent(row: EventRow): CalendarEvent {
  return {
    id: row.id,
    calendarId: row.calendar_id,
    title: row.title,
    startDate: row.start_date,
    isAllDay: row.is_all_day,
    createdBy: row.created_by,
    participants: [...row.participants],
    reminders: (row.reminders as unknown as readonly Reminder[]) ?? [],
    isPinned: row.is_pinned,
    // `is_occurrence` is `not null default false`; the `=== true` coercion is
    // for the two SYNTHESIZED rows below, which are object literals rather than
    // reads and could otherwise leak `undefined` into a `boolean` field.
    isOccurrence: row.is_occurrence === true,
    comments: [],
    attachments: [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.parent_event_id != null && { parentEventId: row.parent_event_id }),
    ...(row.description != null && { description: row.description }),
    ...(row.emoji != null && { emoji: row.emoji }),
    ...(row.end_date != null && { endDate: row.end_date }),
    ...(row.location != null && { location: row.location }),
    ...(row.url != null && { url: row.url }),
    ...(row.label_id != null && { labelId: row.label_id }),
    ...(row.private_notes != null && { privateNotes: row.private_notes }),
    ...(row.recurrence != null && {
      recurrence: row.recurrence as unknown as RecurrenceRule,
    }),
  };
}

/**
 * Build the {@link CalendarEvent} an UNSENT update answers with.
 *
 * Both queued paths in `updateEvent` return this. The row on the server is
 * unchanged and, by definition of how we got here, unreadable — and the store
 * applies its own optimistic patch and discards this value — so the honest
 * answer is the patch itself dressed as an event, not a stale read and not a
 * rejection for a write that is safely queued.
 */
function synthesizeEvent(
  id: string,
  familyId: string,
  patch: TablesUpdate<'events'>
): CalendarEvent {
  const now = new Date().toISOString();
  return rowToEvent({
    ...patch,
    id,
    family_id: familyId,
    calendar_id: '',
    title: patch.title ?? '',
    start_date: patch.start_date ?? now,
    is_all_day: patch.is_all_day ?? false,
    created_by: '',
    participants: patch.participants ? [...patch.participants] : [],
    reminders: patch.reminders ?? ([] as unknown as Json),
    is_pinned: patch.is_pinned ?? false,
    // An offline patch never converts an event into a series occurrence, and
    // the real row's provenance is unreadable from here. `false` is the value
    // the store discards anyway; see the doc comment above.
    is_occurrence: false,
    parent_event_id: null,
    created_at: now,
    updated_at: now,
  } as EventRow);
}

/**
 * Map an {@link UpdateEventDTO} to the `events` COLUMNS it patches.
 *
 * One copy, two callers — the single-row PATCH in `updateEvent` and the
 * `p_patch` jsonb the series RPC applies — precisely so a field that is
 * sanitized on one path cannot silently reach a column raw on the other.
 *
 * `location` and `url` are the two that used to: they sat in the same object
 * literal as `title` and `description` and skipped `sanitizeText` entirely.
 * `url` takes `sanitizeUrl` rather than `sanitizeText` because the danger in a
 * URL is the SCHEME, not markup, and only `sanitizeUrl` refuses
 * `javascript:`/`data:` — the event sheet renders this value as an anchor.
 *
 * `recurrence` reaches the column as a JSON NULL when the DTO carries `null`.
 * That is the „Без повторение" clear, and it is why the DTO's union had to be
 * widened: `!== undefined` lets an explicit null through while an absent key
 * still means "not edited". `attachments` is deliberately absent — it is not a
 * column on `events` and is persisted by `persistAttachments`.
 */
function eventColumnPatch(data: UpdateEventDTO): TablesUpdate<'events'> {
  return {
    ...(data.title !== undefined && { title: sanitizeText(data.title) }),
    ...(data.description !== undefined && {
      description: sanitizeText(data.description),
    }),
    ...(data.emoji !== undefined && { emoji: data.emoji }),
    ...(data.startDate !== undefined && { start_date: data.startDate }),
    ...(data.endDate !== undefined && { end_date: data.endDate }),
    ...(data.isAllDay !== undefined && { is_all_day: data.isAllDay }),
    ...(data.location !== undefined && {
      location: sanitizeText(data.location),
    }),
    ...(data.url !== undefined && { url: sanitizeUrl(data.url) }),
    ...(data.labelId !== undefined && { label_id: data.labelId }),
    ...(data.participants !== undefined && {
      participants: [...data.participants],
    }),
    ...(data.reminders !== undefined && {
      reminders: [...data.reminders] as unknown as Json,
    }),
    ...(data.recurrence !== undefined && {
      recurrence: data.recurrence as unknown as Json,
    }),
    ...(data.isPinned !== undefined && { is_pinned: data.isPinned }),
  };
}

/**
 * Map one series RPC's `jsonb` answer onto domain events.
 *
 * `master` / `exception` / `tail` are whole `events` rows produced by
 * `to_jsonb(e)`, so they go through the SAME `rowToEvent` every read path uses
 * — a second, hand-rolled mapper here is how `is_occurrence` got dropped on the
 * way up the stack the first time.
 */
function toSeriesWriteResult(payload: unknown): SeriesWriteResult {
  const raw = (payload ?? {}) as {
    readonly master?: unknown;
    readonly exception?: unknown;
    readonly tail?: unknown;
    readonly removedIds?: unknown;
  };
  const mapRow = (value: unknown): CalendarEvent | null =>
    value == null ? null : rowToEvent(value as EventRow);
  return {
    master: mapRow(raw.master),
    exception: mapRow(raw.exception),
    tail: mapRow(raw.tail),
    removedIds: Array.isArray(raw.removedIds)
      ? raw.removedIds.filter((v): v is string => typeof v === 'string')
      : [],
  };
}

/** Map an `event_changes` row to the domain record `ActivityFeed` renders. */
function rowToEventChange(row: EventChangeRow): EventChangeRecord {
  return {
    // Safe: every caller filters through TRACKED_FIELD_SET first.
    field: row.field as EventChangedField,
    oldValue: row.old_value,
    newValue: row.new_value,
    // ****************************************************************
    // ***  PASS THROUGH UNCHANGED. NO LOOKUP, NO JOIN, NO MAPPING. ***
    // ****************************************************************
    // `changed_by` is `auth.uid()` = `profiles.id`, and that is ALREADY the key
    // `ActivityFeed`'s memberMap resolves names by, because
    // `mapActiveFamilyMembers` sets `id: m.profileId`. There are TWO types
    // named `FamilyMember` in this repo and the doc comments used to point at
    // the wrong one; translating this to a `family_members.id` is a one-line
    // change that compiles cleanly and renders „Неизвестен" on every row.
    changedBy: row.changed_by,
    // MUST normalise. The client writes `2026-08-23T09:00:00.000Z`; PostgREST
    // returns `2026-08-23T09:00:00+00:00`. Same instant, different STRING — and
    // the string is both ActivityFeed's batch group key and its React key, so
    // without this one batch splits into N rows and the keys churn on refetch.
    changedAt: new Date(row.changed_at).toISOString(),
  };
}

// ============================================
// Supabase Calendar Service
// ============================================

export class SupabaseCalendarService implements ICalendarService {
  private get client() {
    return getSupabaseClient();
  }

  // ============================================
  // Attachment helpers
  // ============================================

  /**
   * Fetch and attach `event_attachments` for a set of events in a single query,
   * returning new event objects with their `attachments` arrays hydrated.
   *
   * TWO queries total, whatever the page holds: one for the rows and one to
   * sign every object they reference. The previous shape awaited
   * `createSignedUrl` per row inside the loop, so an event page with forty
   * attachments made forty sequential HTTP POSTs — and any one of them
   * rejecting took the caller's entire fetch with it.
   */
  private async hydrateAttachments(
    events: readonly CalendarEvent[]
  ): Promise<CalendarEvent[]> {
    if (events.length === 0) {
      return [];
    }
    const familyId = await getCurrentFamilyId();
    const eventIds = events.map((e) => e.id);
    const { data, error } = await this.client
      .from('event_attachments')
      .select('*')
      .eq('family_id', familyId)
      .in('event_id', eventIds)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }

    const rows = data ?? [];
    const signed = await signAttachmentRows(rows);
    reportUnsignedAttachments(rows, signed);

    const byEvent = new Map<string, EventAttachment[]>();
    for (const row of rows) {
      const attachment = rowToAttachment(row, signed);
      const list = byEvent.get(row.event_id);
      if (list) {
        list.push(attachment);
      } else {
        byEvent.set(row.event_id, [attachment]);
      }
    }

    return events.map((event) => {
      const list = byEvent.get(event.id);
      return list ? { ...event, attachments: list } : event;
    });
  }

  /**
   * The storage keys an event's `event_attachments` rows currently name.
   *
   * Reads the rows rather than trusting the caller's in-memory event, because
   * this is the input to a DELETE against the bucket and the row is the only
   * authority on what is actually referenced. A failed read answers `[]` —
   * "nothing is known to be droppable" — so the fail-safe direction is a leaked
   * object, never a deleted one.
   *
   * AND THAT IS WHY THE KEY COMES FROM `storageKeyForRemoval`, NOT FROM
   * `attachmentKey`. Both callers of this method hand the result straight to
   * `removeFiles`, so every value here is a deletion target. The read helper
   * falls back to the stored string verbatim when it cannot recognise a key,
   * and `storageKeyFromValue` cuts a value at its first '?' or '#' — which on
   * a stored signed URL (`…/plan.pdf?token=<jwt>`) or a hand-edited row names
   * a DIFFERENT object that may well exist. The strict helper refuses those
   * outright and answers `''`, which the filter below drops: an attachment
   * whose `file_url` we cannot read unambiguously leaks its object instead of
   * taking someone else's with it. Photos already remove this way
   * (`supabaseAlbumService`); this is the calendar half of the same policy.
   */
  private async attachmentKeysFor(
    eventId: string,
    familyId: string
  ): Promise<readonly string[]> {
    const { data, error } = await this.client
      .from('event_attachments')
      .select('*')
      .eq('family_id', familyId)
      .eq('event_id', eventId);
    if (error) {
      monitoringService.captureError(new Error(error.message), {
        tags: { feature: 'family-calendar', action: 'attachmentKeysFor' },
        extra: { eventId },
        level: 'warning',
      });
      return [];
    }
    return (data ?? [])
      .map((row) => storageKeyForRemoval(ATTACHMENT_BUCKET, row.file_url))
      .filter((key) => key !== '');
  }

  /**
   * Replace the `event_attachments` rows for an event with the provided set.
   * Only attachments that carry a `storagePath` (i.e. went through
   * FileUploadArea and reached the `attachments` bucket) are persisted; any
   * other entry is skipped. Existing rows are deleted first so the table
   * mirrors `attachments`.
   *
   * AND THE OBJECTS GO TOO. Removing an attachment used to delete its row and
   * leave the file in the bucket forever: `storageService`'s removal helpers
   * had zero callers anywhere, and `cleanup-orphan-storage` is scoped to the
   * `photos` bucket by design, so nothing on the project ever collected an
   * `attachments` orphan.
   * The set difference below — keys the rows name, minus keys the incoming
   * attachments name — is exactly the set the user dropped, and every key an
   * `event_attachments` row holds belongs to one event (the upload path writes
   * `<family_id>/<event_id>/<uuid>-<name>`, and the copy-event path starts with
   * an empty attachment list), so there is no second referent to break.
   */
  private async persistAttachments(
    eventId: string,
    familyId: string,
    attachments: readonly EventAttachment[]
  ): Promise<void> {
    const kept = new Set(
      attachments
        .map((a) => a.storagePath)
        .filter((path): path is string => typeof path === 'string')
    );
    const existingKeys = await this.attachmentKeysFor(eventId, familyId);
    const dropped = [...new Set(existingKeys.filter((key) => !kept.has(key)))];

    await this.client
      .from('event_attachments')
      .delete()
      .eq('family_id', familyId)
      .eq('event_id', eventId);

    // `file_name` is the DISPLAY name, taken from `File.name` — the one field
    // on the row that is verbatim user input, and the one the UI prints and
    // `getFileTypeFromExtension` parses. It went in raw while `title` and
    // `description` two literals down went through `sanitizeText`, so it is
    // sanitized here too, with `sanitizeFilename` rather than `sanitizeText`:
    // the risk on a filename is a path (`../`, `/`) or a shell/Windows
    // metacharacter, not markup, and the extension has to survive so the
    // attachment still renders with the right icon.
    const rows: TablesInsert<'event_attachments'>[] = attachments
      .filter((a) => typeof a.storagePath === 'string')
      .map((a) => ({
        event_id: eventId,
        family_id: familyId,
        file_name: sanitizeFilename(a.filename),
        file_url: a.storagePath as string,
        file_size: a.size,
        mime_type: a.mimeType,
        uploaded_by: a.uploadedBy,
      }));

    if (rows.length > 0) {
      const { error } = await this.client
        .from('event_attachments')
        .insert(rows);
      if (error) {
        throw new Error(error.message);
      }
    }

    // Last, and never before the insert: an insert that throws leaves the
    // objects in place (a leak, recoverable) instead of deleting files the
    // surviving rows would have pointed at. `removeFiles` is best-effort by
    // contract — it batches, drops duplicates, reports its own failures to
    // monitoring and never rejects — so no try/catch here, and a dead bucket
    // cannot fail a write that has already committed.
    await removeFiles(ATTACHMENT_BUCKET, dropped);
  }

  /**
   * Fetch and attach `event_comments` for a set of events in a single query.
   */
  private async hydrateComments(
    events: readonly CalendarEvent[]
  ): Promise<CalendarEvent[]> {
    if (events.length === 0) {
      return [];
    }
    const familyId = await getCurrentFamilyId();
    const eventIds = events.map((e) => e.id);
    const { data, error } = await this.client
      .from('event_comments')
      .select('*')
      .eq('family_id', familyId)
      .in('event_id', eventIds)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }

    const byEvent = new Map<string, EventComment[]>();
    for (const row of data ?? []) {
      const comment = rowToComment(row);
      const list = byEvent.get(row.event_id);
      if (list) {
        list.push(comment);
      } else {
        byEvent.set(row.event_id, [comment]);
      }
    }

    return events.map((event) => {
      const list = byEvent.get(event.id);
      return list ? { ...event, comments: list } : event;
    });
  }

  /**
   * Hydrate an event that has just been written, without letting hydration
   * fail the write.
   *
   * The row is committed by the time this runs, so a failure here is not a
   * failed mutation and must not be reported as one — that is exactly how
   * `createEvent` / `updateEvent` came to reject for writes that had actually
   * succeeded, leaving the store to roll an event back out of the UI and toast
   * about it while the row sat in Postgres. The signing half can no longer
   * throw at all (see `rowToAttachment`); the two SELECTs still can, and this
   * absorbs them into the un-hydrated event plus one monitoring event.
   */
  private async hydrateAfterWrite(
    event: CalendarEvent
  ): Promise<CalendarEvent> {
    try {
      const [hydrated] = await this.hydrateComments(
        await this.hydrateAttachments([event])
      );
      return hydrated ?? event;
    } catch (error) {
      monitoringService.captureError(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: { feature: 'family-calendar', action: 'hydrateAfterWrite' },
          extra: { eventId: event.id },
          level: 'warning',
        }
      );
      return event;
    }
  }

  /**
   * Resolve the authenticated profile id (= Supabase auth uid).
   *
   * Both branches are local reads now. {@link getCurrentProfileId} was an
   * `auth.getUser()` round trip, and that round trip is what used to reject
   * inside `createEvent` ABOVE the offline gate — on a captive portal or a dead
   * uplink it threw before either the gate or the transport-failure fallback
   * could queue the insert, and the event was simply lost. It reads the
   * persisted session directly now; the explicit `auth.getSession()` below
   * stays as the offline branch so a resolver failure of any other kind still
   * yields the signed-in uuid rather than costing the enqueue. The todo service
   * resolves its author the same way.
   */
  private async resolveProfileId(): Promise<string> {
    if (!isOffline()) {
      return getCurrentProfileId();
    }
    const { data } = await this.client.auth.getSession();
    const profileId = data.session?.user.id;
    if (!profileId) {
      throw new Error(
        'Няма активна сесия: не може да се определи текущият потребител.'
      );
    }
    return profileId;
  }

  // =====================
  // Offline enqueue paths
  // =====================
  //
  // One per operation, so the `navigator.onLine === false` gate and the
  // transport-failure fallback below it queue byte-identical entries. They were
  // inline before, which meant only the first of the two could exist.

  /**
   * Queue an event INSERT for replay and answer with the optimistic event.
   *
   * Attachments are skipped on both queued paths: the files need a live storage
   * upload, which is the thing that is unavailable.
   */
  private async enqueueEventInsert(
    insert: TablesInsert<'events'>
  ): Promise<CalendarEvent> {
    const now = new Date().toISOString();
    const payload: TablesInsert<'events'> = {
      ...insert,
      id: crypto.randomUUID(),
      // Explicit rather than left to the column default: this payload is also
      // the row `rowToEvent` maps into the optimistic event the caller sees,
      // and a hand-authored event is never a generated occurrence.
      is_occurrence: false,
      created_at: now,
      updated_at: now,
    };
    await enqueueSupabaseMutation({
      table: 'events',
      op: 'insert',
      payload: payload,
      description: `Създаване на събитие „${insert.title}“`,
    });
    return rowToEvent(payload as EventRow);
  }

  /** Queue an event UPDATE for replay. */
  private async enqueueEventUpdate(
    id: string,
    patch: TablesUpdate<'events'>
  ): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'events',
      op: 'update',
      payload: patch,
      matchId: id,
      description: 'Промяна на събитие',
    });
  }

  /** Queue an event DELETE for replay. */
  private async enqueueEventDelete(id: string): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'events',
      op: 'delete',
      matchId: id,
      description: 'Изтриване на събитие',
    });
  }

  // ============================================
  // Calendar Operations
  // ============================================

  private async loadLabels(calendarId: string): Promise<EventLabel[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('calendar_labels')
      .select('*')
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      // `sort_order` is the display order and `created_at` is only the
      // tie-break. It has to be there: the column defaults to 0, so any row
      // written before migration 028 — or by a path that does not compute a
      // position — ties with every other such row, and Postgres may return
      // tied rows in any order it likes. Without the second key the label
      // list could reshuffle itself between two reads of unchanged data.
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToLabel);
  }

  async getCalendars(): Promise<readonly Calendar[]> {
    const familyId = await getCurrentFamilyId();
    // `id` is a tie-break, not decoration. The store picks the family's
    // default write target as "the first calendar in this list", and
    // migration 026 backfills every existing family's calendar in one
    // statement — so `created_at` alone can tie exactly, and Postgres is free
    // to return tied rows in any order. Without the second key, two members
    // of the same family could hydrate the same set of calendars in different
    // orders and default to writing into different ones.
    const { data, error } = await this.client
      .from('calendars')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }

    // Bulk-load share links and group per calendar.
    const { data: linkRows } = await this.client
      .from('calendar_share_links')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: false });
    const linksByCalendar = new Map<string, ShareLink[]>();
    for (const row of linkRows ?? []) {
      const link = rowToShareLink(row);
      const list = linksByCalendar.get(row.calendar_id);
      if (list) {
        list.push(link);
      } else {
        linksByCalendar.set(row.calendar_id, [link]);
      }
    }

    const calendars = await Promise.all(
      (data ?? []).map(async (row) => {
        const labels = await this.loadLabels(row.id);
        const calendar = rowToCalendar(row, labels);
        const shareLinks = linksByCalendar.get(row.id);
        return shareLinks ? { ...calendar, shareLinks } : calendar;
      })
    );
    return calendars;
  }

  async getCalendar(id: string): Promise<Calendar | null> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('calendars')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', id)
      .maybeSingle();
    if (error || !data) {
      return null;
    }
    const labels = await this.loadLabels(data.id);
    return rowToCalendar(data, labels);
  }

  async createCalendar(data: CreateCalendarDTO): Promise<Calendar> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'calendars'> = {
      family_id: familyId,
      name: sanitizeText(data.name),
      member_ids: [],
      is_public: data.isPublic ?? false,
      default_view: 'month',
      week_starts_on: data.weekStartsOn ?? 1,
      ...(data.description !== undefined && {
        description: sanitizeText(data.description),
      }),
      ...(data.coverImage !== undefined && { cover_image: data.coverImage }),
    };
    const { data: row, error } = await this.client
      .from('calendars')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(error?.message ?? 'Неуспешно създаване на календар.');
    }
    return rowToCalendar(row, []);
  }

  async updateCalendar(id: string, data: UpdateCalendarDTO): Promise<Calendar> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'calendars'> = {
      ...(data.name !== undefined && { name: sanitizeText(data.name) }),
      ...(data.description !== undefined && {
        description: sanitizeText(data.description),
      }),
      ...(data.coverImage !== undefined && { cover_image: data.coverImage }),
      ...(data.isPublic !== undefined && { is_public: data.isPublic }),
      ...(data.weekStartsOn !== undefined && {
        week_starts_on: data.weekStartsOn,
      }),
    };
    const { data: row, error } = await this.client
      .from('calendars')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', id)
      .select('*')
      .maybeSingle();
    if (error || !row) {
      throw new Error(`Calendar with id ${id} not found`);
    }
    const labels = await this.loadLabels(row.id);
    return rowToCalendar(row, labels);
  }

  async deleteCalendar(id: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('calendars')
      .delete()
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(`Calendar with id ${id} not found`);
    }
  }

  // ============================================
  // Event Operations
  // ============================================

  async getEvents(
    calendarId: string,
    dateRange?: DateRange
  ): Promise<readonly CalendarEvent[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('events')
      .select('*')
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .order('start_date', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    const events = (data ?? []).map(rowToEvent);
    const hydrated = await this.hydrateComments(
      await this.hydrateAttachments(events)
    );
    return this.filterByDateRange(hydrated, dateRange);
  }

  async getAllEvents(dateRange?: DateRange): Promise<readonly CalendarEvent[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('events')
      .select('*')
      .eq('family_id', familyId)
      .order('start_date', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    const events = (data ?? []).map(rowToEvent);
    const hydrated = await this.hydrateComments(
      await this.hydrateAttachments(events)
    );
    return this.filterByDateRange(hydrated, dateRange);
  }

  private filterByDateRange(
    events: readonly CalendarEvent[],
    dateRange?: DateRange
  ): readonly CalendarEvent[] {
    if (!dateRange) {
      return events;
    }
    return events.filter((e) => {
      const eventDate = toLocalDateKey(e.startDate);
      return eventDate >= dateRange.start && eventDate <= dateRange.end;
    });
  }

  async getEvent(id: string): Promise<CalendarEvent | null> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('events')
      .select('*')
      .eq('family_id', familyId)
      .eq('id', id)
      .maybeSingle();
    if (error || !data) {
      return null;
    }
    const [hydrated] = await this.hydrateComments(
      await this.hydrateAttachments([rowToEvent(data)])
    );
    return hydrated ?? rowToEvent(data);
  }

  async createEvent(data: CreateEventDTO): Promise<CalendarEvent> {
    // Reject a non-uuid `calendarId` here, ABOVE the offline gate.
    //
    // `events.calendar_id` is `uuid NOT NULL`. When the value is not a uuid,
    // online Postgres answers `22P02: invalid input syntax for type uuid` and
    // the caller sees a real failure — but offline the insert is queued
    // instead, the caller is told the event was saved, and the row only dies
    // much later on replay, in the dead-letter queue, with the same 22P02.
    // The queue is being honest there; the value should never have reached
    // it. Failing fast makes the two paths agree: a bad calendar id is a
    // failure the moment it is submitted, online or off.
    if (!isPersistedId(data.calendarId)) {
      throw new Error(
        'Невалиден календар за събитието. Презаредете страницата и опитайте отново.'
      );
    }

    const familyId = await getCurrentFamilyId();
    // Offline-tolerant by construction — `getCurrentProfileId`'s `auth.getUser()`
    // round trip used to reject here, above the gate, so the enqueue below was
    // never reached and the event was simply lost.
    const createdBy = await this.resolveProfileId();

    const insert: TablesInsert<'events'> = {
      family_id: familyId,
      calendar_id: data.calendarId,
      title: sanitizeText(data.title),
      start_date: data.startDate,
      is_all_day: data.isAllDay ?? false,
      created_by: createdBy,
      participants: data.participants ? [...data.participants] : [],
      reminders: (data.reminders ? [...data.reminders] : []) as unknown as Json,
      is_pinned: data.isPinned ?? false,
      ...(data.description !== undefined && {
        description: sanitizeText(data.description),
      }),
      ...(data.emoji !== undefined && { emoji: data.emoji }),
      ...(data.endDate !== undefined && { end_date: data.endDate }),
      // `location` and `url` are free text the user typed, exactly like
      // `title` and `description` above them, and they used to be the two
      // fields in this literal that reached the column raw — so they skipped
      // `sanitizeText`'s tag stripping, control-character normalisation and
      // 10,000-character cap while their neighbours did not.
      //
      // `url` gets `sanitizeUrl`, not `sanitizeText`: the danger in a URL is
      // the scheme, not markup, and only `sanitizeUrl` refuses
      // `javascript:`/`data:` — the event detail sheet renders this value as
      // an anchor. A value it refuses is stored as `''` rather than raw,
      // which is the same trade `sanitizeText` already makes for a
      // description that was nothing but markup: the field is dropped, the
      // write still lands, and nothing dangerous is persisted for a later
      // reader to trust. The create/edit forms validate `url` with Zod's
      // `.url()` first, so a value the user typed in the app does not reach
      // this branch; the sanitizer is here for every other caller.
      ...(data.location !== undefined && {
        location: sanitizeText(data.location),
      }),
      ...(data.url !== undefined && { url: sanitizeUrl(data.url) }),
      ...(data.labelId !== undefined && { label_id: data.labelId }),
      ...(data.recurrence !== undefined && {
        recurrence: data.recurrence as unknown as Json,
      }),
    };

    // Offline: persist the insert for replay and return an optimistic event.
    if (isOffline()) {
      return this.enqueueEventInsert(insert);
    }

    const {
      data: row,
      error,
      status,
    } = await this.client.from('events').insert(insert).select('*').single();
    if (error || !row) {
      // The browser said "online" and the write still never landed — queue it
      // rather than losing it. A refusal falls through and throws.
      if (isTransientWriteFailure(status, error)) {
        return this.enqueueEventInsert(insert);
      }
      throw new Error(
        error?.message ?? `Calendar with id ${data.calendarId} not found`
      );
    }
    if (data.attachments && data.attachments.length > 0) {
      await this.persistAttachments(row.id, familyId, data.attachments);
    }
    return this.hydrateAfterWrite(rowToEvent(row));
  }

  async updateEvent(id: string, data: UpdateEventDTO): Promise<CalendarEvent> {
    const familyId = await getCurrentFamilyId();
    const patch = eventColumnPatch(data);

    // Offline: queue the patch for replay; report optimistic success. The
    // store applies updates optimistically and discards this return value, so
    // an event synthesized from the patch is sufficient — and nothing is read
    // back, because the `getEvent` that used to run here, after the enqueue and
    // over a network that is by definition down, was a guaranteed failure
    // dressed up as a lookup. Attachments cannot be queued and are skipped.
    if (isOffline()) {
      await this.enqueueEventUpdate(id, patch);
      return synthesizeEvent(id, familyId, patch);
    }

    const {
      data: row,
      error,
      status,
    } = await this.client
      .from('events')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', id)
      .select('*')
      .maybeSingle();
    // `error === null` with no row is an RLS-filtered UPDATE, not a wire
    // failure — it must never be queued, so the classifier is only consulted
    // when there is an error envelope to classify.
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueEventUpdate(id, patch);
      return synthesizeEvent(id, familyId, patch);
    }
    if (error || !row) {
      throw new Error(`Event with id ${id} not found`);
    }
    if (data.attachments !== undefined) {
      await this.persistAttachments(row.id, familyId, data.attachments);
    }
    return this.hydrateAfterWrite(rowToEvent(row));
  }

  /**
   * Delete an event, and with it the objects its attachments occupied.
   *
   * `event_attachments.event_id` is `on delete cascade`, so the rows vanish
   * with the event and nothing is left that names the files — and the only
   * bucket sweeper on the project, `cleanup-orphan-storage`, is scoped to
   * `photos`. The keys are therefore read BEFORE the delete (afterwards there
   * is nothing to read) and the objects are removed AFTER it succeeds, so a
   * refused or queued delete never destroys a file for a row that is still
   * there.
   *
   * NOT covered: the offline branch. A queued delete replays through
   * `offlineMutationQueue`, which speaks only PostgREST and has no storage
   * client, so an event deleted offline still leaks its attachment objects.
   */
  async deleteEvent(id: string): Promise<void> {
    // Offline: queue the delete for replay; report optimistic success. The
    // family scope is resolved BELOW the gate — the queued entry matches on the
    // primary key alone and never needed it.
    if (isOffline()) {
      await this.enqueueEventDelete(id);
      return;
    }
    const familyId = await getCurrentFamilyId();
    const orphanedKeys = await this.attachmentKeysFor(id, familyId);

    const REFUSED_MESSAGE = 'Събитието не можа да бъде изтрито: нямате права.';

    // `.select('id')` is what turns "the request did not error" into "the row is
    // gone". Without it a refused delete is indistinguishable from a successful
    // one on the wire — and this method then ran `removeFiles` on the strength
    // of a falsy `error`, DESTROYING the attachment objects of a row that was
    // still sitting in Postgres. That is the exact shape Wave 7 fixed
    // everywhere else; this was the one file that PR did not touch.
    const { data, error, status } = await this.client
      .from('events')
      .delete()
      .eq('family_id', familyId)
      .eq('id', id)
      .select('id');
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueEventDelete(id);
      return;
    }
    if (error) {
      // The row is still there and still references these objects. Removing
      // them now would delete live attachments out from under an event that
      // still exists — the one case where "best effort" is actively wrong.
      throw new Error(REFUSED_MESSAGE, { cause: error });
    }
    if (!data || data.length === 0) {
      const {
        data: survivor,
        error: confirmError,
        status: confirmStatus,
      } = await this.client
        .from('events')
        .select('id')
        .eq('family_id', familyId)
        .eq('id', id)
        .maybeSingle();
      const verdict = classifyZeroRowDelete(
        survivor !== null,
        confirmError,
        confirmStatus
      );
      if (verdict === 'transient') {
        await this.enqueueEventDelete(id);
        return;
      }
      if (verdict === 'refused') {
        throw new Error(REFUSED_MESSAGE);
      }
      // 'already-gone': another member deleted this event first, so its
      // `event_attachments` rows went with it by cascade and the keys the
      // pre-read collected now name nothing. Falling through to the removal
      // reclaims them; it is the one case where they are provably unreferenced.
    }
    await removeFiles(ATTACHMENT_BUCKET, orphanedKeys);
  }

  // ============================================
  // Series Operations (Wave 8, Major 2)
  // ============================================

  /**
   * Apply a scoped EDIT to a recurring series through
   * `public.update_event_series`.
   *
   * EVERY REFUSAL MUST THROW. `persistCalendarMutation` is fire-and-forget: its
   * `.catch` runs the rollback, sets `state.error` and raises the destructive
   * toast, and a RESOLVED promise can never reach it. Any method here that
   * resolves on a refused write inherits the silent-failure bug by
   * construction — which is also why every authorising statement inside the
   * plpgsql body raises on `row_count = 0` rather than returning quietly.
   *
   * AN EDIT DELETES ROWS TOO, AND THEIR OBJECTS GO WITH THEM. This is not a
   * read-only verb: E-d branch 2a wipes every generated child when the schedule
   * or the rule changes, E-a and E-b delete a child standing on the instant the
   * leg is about to occupy, and E-c takes every child at or after the cut. Each
   * of those cascades away the row's `event_attachments` records and leaves the
   * STORAGE OBJECTS behind, and nothing else on this project sweeps the
   * `attachments` bucket (`cleanup-orphan-storage` is scoped to `photos`), so
   * the leak is permanent. This leg used to have no sweep at all.
   *
   * Same shape and same ORDER as
   * {@link SupabaseCalendarService.deleteEventSeries}, and for the same reason
   * (risk 12): the keys are read BEFORE the RPC, because afterwards the rows
   * that name them are gone, and objects are removed only AFTER it resolves and
   * only for the ids `removedIds` REPORTS. Driving the sweep off the report
   * rather than off which rows we believe the leg chooses is what makes it
   * survive the SQL changing its mind about that — and a refusal, which throws
   * above, destroys nothing at all.
   */
  async updateEventSeries(
    target: SeriesTarget,
    scope: RecurringEventUpdateScope,
    data: UpdateEventDTO,
    instants: SeriesInstants
  ): Promise<SeriesWriteResult> {
    if (isOffline()) {
      throw new Error(SERIES_OFFLINE_MESSAGE);
    }

    // The patch travels as SNAKE_CASE COLUMN VALUES, already sanitized by the
    // same helper the single-row PATCH uses. jsonb also fixes a live typing
    // hole for this path: `p_patch ? 'recurrence' and p_patch->'recurrence' =
    // 'null'::jsonb` expresses "clear the rule", which no DTO shape can.
    const patch = eventColumnPatch(data);

    const familyId = await getCurrentFamilyId();
    const keysByEvent = await this.attachmentKeysForSeries(
      target.masterId,
      familyId
    );

    const { data: payload, error } = await this.client.rpc(
      'update_event_series',
      {
        p_master_id: target.masterId,
        p_scope: scope,
        // `all` has no target instant; the SQL parameter is REQUIRED but
        // NULLABLE, so omitting it would be a PostgREST-level error while null
        // is the value the function expects.
        p_occurrence_start: scope === 'all' ? null : target.occurrenceStart,
        p_patch: patch as unknown as Json,
        p_instants: instants as unknown as Json,
      }
    );
    if (error) {
      // Translated HERE, at the boundary the server text arrives on, rather
      // than in the store: `persistCalendarMutation` renders `error.message`
      // verbatim, and it is not the only caller.
      throw new Error(seriesRefusalMessage(error));
    }

    const result = toSeriesWriteResult(payload);
    const orphanedKeys = result.removedIds.flatMap(
      (id) => keysByEvent.get(id) ?? []
    );
    if (orphanedKeys.length > 0) {
      await removeFiles(ATTACHMENT_BUCKET, orphanedKeys);
    }
    return result;
  }

  /**
   * Apply a scoped DELETE to a recurring series through
   * `public.delete_event_series`. Throws on every refusal, for the reason in
   * {@link SupabaseCalendarService.updateEventSeries}.
   *
   * ATTACHMENTS, AND THE ORDER THEY GO IN. Every scope here deletes ROWS —
   * `all` takes the master and, through `events_parent_event_id_fkey`'s ON
   * DELETE CASCADE, every child; `this_and_following` takes every child at or
   * after the cut; `this` takes the one occurrence — and each of those cascades
   * away the row's `event_attachments` records while leaving the STORAGE
   * OBJECTS behind. Nothing else on this project sweeps the `attachments`
   * bucket (`cleanup-orphan-storage` is scoped to `photos`), so a leak here is
   * permanent. The narrower scopes used to leak unconditionally.
   *
   * The order is what makes it safe, and it is risk 12's lesson: PostgREST has
   * no subselect, so the keys are read BEFORE the RPC (afterwards the rows are
   * gone and there is nothing to read), and objects are removed only AFTER it
   * resolves — and then only for the rows the RPC itself REPORTS as deleted.
   * `removedIds` is the authoritative list every leg returns; filtering by it
   * means a leg that deleted nothing removes nothing, and a refusal, which
   * throws above, destroys nothing at all.
   */
  async deleteEventSeries(
    target: SeriesTarget,
    scope: RecurringEventUpdateScope,
    instants: SeriesInstants
  ): Promise<SeriesWriteResult> {
    if (isOffline()) {
      throw new Error(SERIES_OFFLINE_MESSAGE);
    }

    const familyId = await getCurrentFamilyId();
    const keysByEvent = await this.attachmentKeysForSeries(
      target.masterId,
      familyId
    );

    const { data: payload, error } = await this.client.rpc(
      'delete_event_series',
      {
        p_master_id: target.masterId,
        p_scope: scope,
        p_occurrence_start: scope === 'all' ? null : target.occurrenceStart,
        p_instants: instants as unknown as Json,
      }
    );
    if (error) {
      // See updateEventSeries: Bulgarian copy by CODE, never the raw text.
      throw new Error(seriesRefusalMessage(error));
    }

    const result = toSeriesWriteResult(payload);
    const orphanedKeys = result.removedIds.flatMap(
      (id) => keysByEvent.get(id) ?? []
    );
    if (orphanedKeys.length > 0) {
      await removeFiles(ATTACHMENT_BUCKET, orphanedKeys);
    }
    return result;
  }

  /**
   * Attachment object keys for a master AND its occurrences, BY EVENT ID.
   *
   * Keyed rather than flattened because both callers — the DELETE leg and the
   * EDIT leg, which deletes rows of its own — may only remove the objects of
   * the rows the RPC actually reported deleting. Scope `this` deletes one row
   * out of ninety, and a flat list would take the whole series' files with it.
   *
   * Two reads, not one: `event_attachments` is joined to `events` by
   * `event_id`, PostgREST has no subselect, and `id = M or parent_event_id = M`
   * is not expressible as a single filter over the attachments table. Best
   * effort, like {@link SupabaseCalendarService.attachmentKeysFor} — a read
   * that fails leaves objects behind rather than taking the delete down with
   * it.
   */
  private async attachmentKeysForSeries(
    masterId: string,
    familyId: string
  ): Promise<ReadonlyMap<string, readonly string[]>> {
    const { data: children, error: childError } = await this.client
      .from('events')
      .select('id')
      .eq('family_id', familyId)
      .eq('parent_event_id', masterId);
    if (childError) {
      monitoringService.captureError(new Error(childError.message), {
        tags: { feature: 'family-calendar', action: 'attachmentKeysForSeries' },
        extra: { masterId },
        level: 'warning',
      });
    }
    const ids = [masterId, ...(children ?? []).map((row) => row.id)];

    const { data, error } = await this.client
      .from('event_attachments')
      .select('*')
      .eq('family_id', familyId)
      .in('event_id', ids);
    if (error) {
      monitoringService.captureError(new Error(error.message), {
        tags: { feature: 'family-calendar', action: 'attachmentKeysForSeries' },
        extra: { masterId },
        level: 'warning',
      });
      return new Map();
    }

    const byEvent = new Map<string, string[]>();
    for (const row of data ?? []) {
      const key = storageKeyForRemoval(ATTACHMENT_BUCKET, row.file_url);
      if (key === '') continue;
      const list = byEvent.get(row.event_id);
      if (list) list.push(key);
      else byEvent.set(row.event_id, [key]);
    }
    return byEvent;
  }

  // ============================================
  // Event Change History (Wave 8, Major 3)
  // ============================================

  async getEventChanges(): Promise<
    Readonly<Record<string, readonly EventChangeRecord[]>>
  > {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('event_changes')
      .select('*')
      .eq('family_id', familyId)
      // DESCENDING plus an EXPLICIT limit. See EVENT_CHANGE_PAGE_SIZE: an
      // unbounded ascending read is trimmed at the server's `max_rows` and
      // returns the OLDEST 1000 rows, silently, which is the exact opposite of
      // what the feed is for.
      .order('changed_at', { ascending: false })
      .limit(EVENT_CHANGE_PAGE_SIZE);
    if (error) throw new Error(error.message);

    const byEvent = new Map<string, EventChangeRecord[]>();
    for (const row of data ?? []) {
      // DROP, do not cast. This is the read-side guard that pays for the DDL's
      // decision to shape-check `field` rather than enumerate it.
      if (!TRACKED_FIELD_SET.has(row.field)) continue;
      const list = byEvent.get(row.event_id);
      if (list) list.push(rowToEventChange(row));
      else byEvent.set(row.event_id, [rowToEventChange(row)]);
    }

    // Rows arrived newest-first; the store's local log appends
    // chronologically. Reversing makes a persisted array shaped exactly like a
    // locally-produced one, so `ActivityFeed` behaves identically whichever it
    // is handed — it sorts its assembled items itself, so order only has to be
    // CONSISTENT.
    const out: Record<string, readonly EventChangeRecord[]> = {};
    for (const [eventId, list] of byEvent) out[eventId] = list.reverse();
    return out;
  }

  /**
   * Persist one edit's field-level history rows.
   *
   * ***  A HISTORY ROW MUST NEVER FAIL THE EDIT THAT PRODUCED IT.  ***
   * This runs inside the SAME `persistCalendarMutation` closure as the PATCH
   * above it, and that PATCH RESOLVES when it is queued offline. Left to throw
   * on an unreachable network, this rejected the whole closure: the store rolled
   * the edit back, the user got a destructive „не можа да бъде запазена" toast —
   * and then the queued PATCH replayed on reconnect and the change landed
   * anyway. A false failure for a write that succeeds is the worst of the four
   * outcomes.
   *
   * So the transport class is SKIPPED, not queued and not raised. Not queued
   * because it CANNOT be: `event_changes` is not in the queue's
   * `REPLAYABLE_TABLES`, and a batch is an ARRAY while `checkReplayable`
   * requires a single row object — an entry minted here would be dead-lettered
   * on replay, which is user-visible (destructive toast + Sentry) for an audit
   * row nobody asked about. The local optimistic record survives in
   * `eventChangeLog` and the next `load()` REPLACES that map wholesale, so the
   * phantom disappears on its own rather than lingering as history the database
   * does not hold. This is the same call the offline paths already make about
   * attachments, for the same reason: the thing that is unavailable is the
   * network, and the edit itself is not.
   *
   * EVERY OTHER FAILURE STILL THROWS. An RLS refusal, a constraint violation, a
   * 400 — those mean the row was REJECTED rather than undelivered, and resolving
   * on a refused write is the silent-failure bug this whole file is built to
   * avoid.
   */
  async createEventChanges(
    eventId: string,
    records: readonly EventChangeRecord[]
  ): Promise<void> {
    if (records.length === 0) return;
    // Offline: skip. The PATCH this batch describes has already been queued and
    // has already resolved, so throwing here would roll back a write that is
    // going to land.
    if (isOffline()) {
      reportChangeLogSkipped('offline', 'navigator.onLine === false');
      return;
    }
    const familyId = await getCurrentFamilyId();

    const rows: TablesInsert<'event_changes'>[] = records.map((record) => ({
      family_id: familyId,
      event_id: eventId,
      field: record.field,
      old_value: record.oldValue,
      new_value: record.newValue,
      // `changed_by = auth.uid()` is enforced by event_changes_insert_member,
      // so a record carrying anybody else's id is refused rather than
      // mis-attributed. The store refuses to BUILD one.
      changed_by: record.changedBy,
      // Written by the client even though the column defaults to now(): every
      // record in one edit batch must share the exact string ActivityFeed
      // groups and keys on, and a server default would give each row its own.
      changed_at: record.changedAt,
    }));

    const { error, status } = await this.client
      .from('event_changes')
      .insert(rows);
    if (error && !isTransientWriteFailure(status, error)) {
      // ***  BULGARIAN OUT, ENGLISH TO SENTRY. NEVER `error.message`.  ***
      // This rejection is rendered VERBATIM as a toast description in a
      // Bulgarian-only app. `error.message` here is PostgREST prose — „new row
      // violates row-level security policy for table \"event_changes\"" — which
      // is both English and about a table the family has never heard of. The
      // raw text is not thrown away, it is reported: an audit row the database
      // refuses is a gap somebody has to be able to find later.
      monitoringService.captureError(new Error(error.message), {
        tags: { feature: 'family-calendar', action: 'createEventChanges' },
        extra: { eventId, rows: rows.length },
        level: 'warning',
      });
      throw new Error(EVENT_CHANGE_WRITE_FAILED_MESSAGE);
    }
    if (error) {
      reportChangeLogSkipped('transport', error.message);
    }
  }

  // ============================================
  // Calendar Member Operations
  // ============================================

  async getMembers(calendarId: string): Promise<readonly CalendarMember[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('calendar_members')
      .select('*')
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToMember);
  }

  async addMember(
    calendarId: string,
    data: AddMemberDTO
  ): Promise<CalendarMember> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'calendar_members'> = {
      family_id: familyId,
      calendar_id: calendarId,
      profile_id: data.memberId,
      role: data.role ?? 'viewer',
    };
    const { data: row, error } = await this.client
      .from('calendar_members')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(
        error?.message ?? `Calendar with id ${calendarId} not found`
      );
    }

    // Also append to calendars.member_ids, which is read alongside the
    // calendar_members rows.
    await this.appendCalendarMemberId(familyId, calendarId, data.memberId);

    return rowToMember(row);
  }

  private async appendCalendarMemberId(
    familyId: string,
    calendarId: string,
    memberId: string
  ): Promise<void> {
    const { data: cal } = await this.client
      .from('calendars')
      .select('member_ids')
      .eq('family_id', familyId)
      .eq('id', calendarId)
      .maybeSingle();
    if (cal && !cal.member_ids.includes(memberId)) {
      await this.client
        .from('calendars')
        .update({ member_ids: [...cal.member_ids, memberId] })
        .eq('family_id', familyId)
        .eq('id', calendarId);
    }
  }

  async removeMember(calendarId: string, memberId: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('calendar_members')
      .delete()
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .eq('profile_id', memberId);
    if (error) {
      throw new Error(`Member ${memberId} is not in calendar ${calendarId}`);
    }

    const { data: cal } = await this.client
      .from('calendars')
      .select('member_ids')
      .eq('family_id', familyId)
      .eq('id', calendarId)
      .maybeSingle();
    if (cal) {
      await this.client
        .from('calendars')
        .update({ member_ids: cal.member_ids.filter((id) => id !== memberId) })
        .eq('family_id', familyId)
        .eq('id', calendarId);
    }
  }

  async updateMemberRole(
    calendarId: string,
    memberId: string,
    role: CalendarMemberRole
  ): Promise<CalendarMember> {
    const familyId = await getCurrentFamilyId();
    const { data: row, error } = await this.client
      .from('calendar_members')
      .update({ role })
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .eq('profile_id', memberId)
      .select('*')
      .maybeSingle();
    if (error || !row) {
      throw new Error(`Member ${memberId} is not in calendar ${calendarId}`);
    }
    return rowToMember(row);
  }

  // ============================================
  // Label Operations
  // ============================================

  async getLabels(calendarId: string): Promise<readonly EventLabel[]> {
    return this.loadLabels(calendarId);
  }

  async createLabel(
    calendarId: string,
    data: CreateLabelDTO
  ): Promise<EventLabel> {
    const familyId = await getCurrentFamilyId();

    const existing = await this.loadLabels(calendarId);
    const nextSortOrder = existing.length;

    const insert: TablesInsert<'calendar_labels'> = {
      family_id: familyId,
      calendar_id: calendarId,
      name: sanitizeText(data.name),
      color: data.color,
      emoji: labelEmojiForColumn(data.emoji),
      sort_order: nextSortOrder,
    };
    const { data: row, error } = await this.client
      .from('calendar_labels')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(
        error?.message ?? `Calendar with id ${calendarId} not found`
      );
    }
    // The returned label is whatever the DATABASE now holds — no spreading of
    // `data.emoji` back over it. That echo is what made this bug invisible:
    // the reconcile swapped in a label carrying an emoji no column held, the
    // chip looked right for the rest of the session, and the emoji vanished
    // on the first refetch (tab focus, 30 s throttle) with no event to blame.
    return rowToLabel(row);
  }

  async updateLabel(id: string, data: UpdateLabelDTO): Promise<EventLabel> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'calendar_labels'> = {
      ...(data.name !== undefined && { name: sanitizeText(data.name) }),
      ...(data.color !== undefined && { color: data.color }),
      // `undefined` means "not edited" and must not reach the patch; `''` and
      // `null` both mean "cleared" and must reach it as NULL. Those are three
      // different states and the old conditional spread collapsed two of them,
      // so an emoji could be set but never removed.
      ...(data.emoji !== undefined && {
        emoji: labelEmojiForColumn(data.emoji),
      }),
      ...(data.sortOrder !== undefined && { sort_order: data.sortOrder }),
    };
    const { data: row, error } = await this.client
      .from('calendar_labels')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', id)
      .select('*')
      .maybeSingle();
    if (error || !row) {
      throw new Error(`Label with id ${id} not found`);
    }
    // As in `createLabel`: the row is the answer. The old `rowToLabel(row, 0)`
    // also invented a sort order — every edited label claimed position 0.
    return rowToLabel(row);
  }

  async deleteLabel(id: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('calendar_labels')
      .delete()
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(`Label with id ${id} not found`);
    }
  }

  // ============================================
  // Event Comment Operations
  // ============================================

  async addEventComment(
    eventId: string,
    text: string,
    authorId: string
  ): Promise<EventComment> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'event_comments'> = {
      family_id: familyId,
      event_id: eventId,
      author_id: authorId,
      text: sanitizeText(text),
    };
    const { data: row, error } = await this.client
      .from('event_comments')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(error?.message ?? 'Коментарът не можа да бъде запазен.');
    }
    return rowToComment(row);
  }

  async updateEventComment(commentId: string, text: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('event_comments')
      .update({ text: sanitizeText(text) })
      .eq('family_id', familyId)
      .eq('id', commentId);
    if (error) {
      throw new Error(error.message);
    }
  }

  async deleteEventComment(commentId: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('event_comments')
      .delete()
      .eq('family_id', familyId)
      .eq('id', commentId);
    if (error) {
      throw new Error(error.message);
    }
  }

  // ============================================
  // Calendar Member Overrides
  // ============================================

  async updateMemberOverrides(
    calendarId: string,
    memberId: string,
    overrides: UpdateMemberOverridesDTO
  ): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'calendar_members'> = {
      ...(overrides.displayNameOverride !== undefined && {
        display_name_override: overrides.displayNameOverride,
      }),
      ...(overrides.avatarOverride !== undefined && {
        avatar_override: overrides.avatarOverride,
      }),
    };
    const { error } = await this.client
      .from('calendar_members')
      .update(patch)
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .eq('profile_id', memberId);
    if (error) {
      throw new Error(error.message);
    }
  }

  // ============================================
  // Share Link Operations
  // ============================================

  async getShareLinks(calendarId: string): Promise<readonly ShareLink[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('calendar_share_links')
      .select('*')
      .eq('family_id', familyId)
      .eq('calendar_id', calendarId)
      .order('created_at', { ascending: false });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToShareLink);
  }

  async createShareLink(
    calendarId: string,
    _permission: ShareLinkPermission,
    expiration: ShareLinkExpiration,
    createdById: string,
    createdByName?: string
  ): Promise<ShareLink> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'calendar_share_links'> = {
      family_id: familyId,
      calendar_id: calendarId,
      token: generateShareToken(),
      created_by: createdById,
      expires_at: expirationToTimestamp(expiration),
    };
    const { data: row, error } = await this.client
      .from('calendar_share_links')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(error?.message ?? 'Връзката не можа да бъде създадена.');
    }
    const link = rowToShareLink(row);
    return createdByName ? { ...link, createdByName } : link;
  }

  async revokeShareLink(linkId: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('calendar_share_links')
      .update({ revoked_at: new Date().toISOString() })
      .eq('family_id', familyId)
      .eq('id', linkId);
    if (error) {
      throw new Error(error.message);
    }
  }

  // ============================================
  // Memo Operations
  // ============================================

  async getMemos(): Promise<readonly Memo[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('memos')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToMemo);
  }

  async createMemo(data: CreateMemoDTO): Promise<Memo> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'memos'> = {
      family_id: familyId,
      calendar_id: data.calendarId,
      author_id: data.authorId,
      title: sanitizeText(data.title),
      content: sanitizeText(data.content),
      color: memoColorForColumn(data.color),
      is_pinned: data.isPinned ?? false,
    };
    const { data: row, error } = await this.client
      .from('memos')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(error?.message ?? 'Бележката не можа да бъде запазена.');
    }
    return rowToMemo(row);
  }

  async updateMemo(id: string, updates: UpdateMemoDTO): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'memos'> = {
      ...(updates.title !== undefined && {
        title: sanitizeText(updates.title),
      }),
      ...(updates.content !== undefined && {
        content: sanitizeText(updates.content),
      }),
      ...(updates.color !== undefined && {
        color: memoColorForColumn(updates.color),
      }),
      ...(updates.isPinned !== undefined && { is_pinned: updates.isPinned }),
    };
    const { error } = await this.client
      .from('memos')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(error.message);
    }
  }

  async deleteMemo(id: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('memos')
      .delete()
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(error.message);
    }
  }

  // ============================================
  // Anniversary Operations
  // ============================================

  async getAnniversaries(): Promise<readonly Anniversary[]> {
    const familyId = await getCurrentFamilyId();
    const { data, error } = await this.client
      .from('anniversaries')
      .select('*')
      .eq('family_id', familyId)
      .order('date', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToAnniversary);
  }

  async createAnniversary(data: CreateAnniversaryDTO): Promise<Anniversary> {
    const familyId = await getCurrentFamilyId();
    const insert: TablesInsert<'anniversaries'> = {
      family_id: familyId,
      calendar_id: data.calendarId,
      created_by: data.createdBy,
      name: sanitizeText(data.name),
      date: data.date,
      type: data.type,
      category: anniversaryCategoryForColumn(data.category),
      emoji: data.emoji ?? '🎉',
      color: typeof data.color === 'string' ? data.color : 'blue',
      is_recurring: data.isRecurring ?? false,
      notify_on_day: data.notifyOnDay ?? true,
      ...(data.description !== undefined && {
        description: sanitizeText(data.description),
      }),
    };
    const { data: row, error } = await this.client
      .from('anniversaries')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      throw new Error(
        error?.message ?? 'Годишнината не можа да бъде запазена.'
      );
    }
    return rowToAnniversary(row);
  }

  async updateAnniversary(
    id: string,
    updates: UpdateAnniversaryDTO
  ): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const patch: TablesUpdate<'anniversaries'> = {
      ...(updates.name !== undefined && { name: sanitizeText(updates.name) }),
      ...(updates.date !== undefined && { date: updates.date }),
      ...(updates.type !== undefined && { type: updates.type }),
      ...(updates.category !== undefined && {
        category: anniversaryCategoryForColumn(updates.category),
      }),
      ...(updates.description !== undefined && {
        description: sanitizeText(updates.description),
      }),
      ...(updates.emoji !== undefined && { emoji: updates.emoji }),
      ...(updates.color !== undefined && {
        color: typeof updates.color === 'string' ? updates.color : 'blue',
      }),
      ...(updates.isRecurring !== undefined && {
        is_recurring: updates.isRecurring,
      }),
      ...(updates.notifyOnDay !== undefined && {
        notify_on_day: updates.notifyOnDay,
      }),
    };
    const { error } = await this.client
      .from('anniversaries')
      .update(patch)
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(error.message);
    }
  }

  async deleteAnniversary(id: string): Promise<void> {
    const familyId = await getCurrentFamilyId();
    const { error } = await this.client
      .from('anniversaries')
      .delete()
      .eq('family_id', familyId)
      .eq('id', id);
    if (error) {
      throw new Error(error.message);
    }
  }
}
